"use client";

import * as React from "react";
import { useIsMobile } from "../../hooks/use-media-query";
import { SidebarContext } from "../../components/ui/sidebar";
import type { Omittable } from "../../lib/props";

/**
 * Shell state shared with descendants: the desktop expanded/collapsed pane
 * (`open`) and the mobile drawer (`openMobile`) are separate — one is a
 * persisted layout preference, the other is ephemeral presentation. Mirrors the
 * fields a consumer reaches for through {@link useAppShell}.
 */
export type AppShellContextValue = {
  isMobile: boolean;
  /** Desktop only: the module-menu pane is expanded vs collapsed to the rail. */
  open: boolean;
  setOpen: (open: boolean) => void;
  /** Mobile only: the nav drawer is presented vs dismissed. */
  openMobile: boolean;
  setOpenMobile: (open: boolean) => void;
  /** Derived from `open` — `data-state` styling-hook parity with the base parts. */
  state: "expanded" | "collapsed";
  /** Toggles the drawer on mobile, the desktop pane otherwise. */
  toggleSidebar: () => void;
  /** Current pane width in px — the pane alone, NOT including the rail. */
  sidebarWidth: number;
  /** Streams a dragged pane width. Clamped by the caller of the seam. */
  setSidebarWidth: (width: number) => void;
  /** Clamp bounds a resize seam must honour. */
  minSidebarWidth: number;
  maxSidebarWidth: number;
  /**
   * True while a seam is dragging. The shell drops its width transition for
   * the duration — otherwise the pane eases toward each streamed width and
   * trails the pointer by the animation duration.
   */
  isResizingSidebar: boolean;
  setResizingSidebar: (resizing: boolean) => void;
};

const AppShellContext = React.createContext<AppShellContextValue | null>(null);

export function useAppShell(): AppShellContextValue {
  const context = React.useContext(AppShellContext);
  if (!context) {
    throw new Error("useAppShell must be used within an AppShellProvider.");
  }
  return context;
}

export type AppShellProviderProps = Omittable<{
  /** Initial desktop pane state for uncontrolled mode. Defaults to `true`. */
  defaultOpen?: boolean;
  /** Controlled desktop pane state — pass alongside `onOpenChange`. */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** Initial pane width for uncontrolled mode. Defaults to 250. */
  defaultSidebarWidth?: number;
  /** Controlled pane width — pass alongside `onSidebarWidthChange`. */
  sidebarWidth?: number;
  onSidebarWidthChange?: (width: number) => void;
  /** Clamp bounds published to any resize seam. Default 120 / 400. */
  minSidebarWidth?: number;
  maxSidebarWidth?: number;
  children: React.ReactNode;
}>;

/**
 * Owns the shell's open state and exposes it via {@link useAppShell}. Desktop
 * `open` is controllable (the layout persists it); mobile `openMobile` is always
 * internal and ephemeral. No cookie, no keyboard shortcut — the app drives
 * persistence and the module-menu header owns the collapse affordance.
 */
export function AppShellProvider({
  defaultOpen = true,
  open: openProp,
  onOpenChange,
  defaultSidebarWidth = 250,
  sidebarWidth: widthProp,
  onSidebarWidthChange,
  minSidebarWidth = 120,
  maxSidebarWidth = 400,
  children,
}: AppShellProviderProps): React.ReactElement {
  const isMobile = useIsMobile();
  const [openMobile, setOpenMobile] = React.useState(false);

  const [uncontrolledOpen, setUncontrolledOpen] = React.useState(defaultOpen);
  const open = openProp ?? uncontrolledOpen;

  const [uncontrolledWidth, setUncontrolledWidth] =
    React.useState(defaultSidebarWidth);
  const sidebarWidth = widthProp ?? uncontrolledWidth;
  const [isResizingSidebar, setResizingSidebar] = React.useState(false);

  const setSidebarWidth = React.useCallback(
    (value: number) => {
      if (onSidebarWidthChange) {
        onSidebarWidthChange(value);
      } else {
        setUncontrolledWidth(value);
      }
    },
    [onSidebarWidthChange],
  );

  const setOpen = React.useCallback(
    (value: boolean) => {
      if (onOpenChange) {
        onOpenChange(value);
      } else {
        setUncontrolledOpen(value);
      }
    },
    [onOpenChange],
  );

  const toggleSidebar = React.useCallback(() => {
    if (isMobile) {
      setOpenMobile((prev) => !prev);
    } else {
      setOpen(!open);
    }
  }, [isMobile, open, setOpen]);

  const value = React.useMemo<AppShellContextValue>(
    () => ({
      isMobile,
      open,
      openMobile,
      setOpen,
      setOpenMobile,
      state: open ? "expanded" : "collapsed",
      toggleSidebar,
      sidebarWidth,
      setSidebarWidth,
      minSidebarWidth,
      maxSidebarWidth,
      isResizingSidebar,
      setResizingSidebar,
    }),
    [
      isMobile,
      open,
      openMobile,
      setOpen,
      toggleSidebar,
      sidebarWidth,
      setSidebarWidth,
      minSidebarWidth,
      maxSidebarWidth,
      isResizingSidebar,
    ],
  );

  // The nav rows inside the module-menu pane are the base `SidebarMenu*`
  // primitives, and `SidebarMenuButton` calls `useSidebar()` unconditionally.
  // We drive our own shell rather than the base `Sidebar`, so publish the base
  // `SidebarContext` from here too — its shape is identical — so those rows
  // resolve their context instead of throwing.
  return (
    <AppShellContext.Provider value={value}>
      <SidebarContext.Provider value={value}>
        {children}
      </SidebarContext.Provider>
    </AppShellContext.Provider>
  );
}
